import { useCallback, useState } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Button } from '@components/button';
import { IconInfoSmall } from '@components/icons/info-small';
import { Select } from '@components/select';
import { Stack } from '@components/stack';
import { Switch } from '@components/switch';
import { Text } from '@components/text';
import { useToaster } from '@components/toaster';

import { api } from 'api';

import { Department, SubmitError } from 'api/departments/requests';
import { Loading } from 'components/Loading';
import { ModalProps } from 'features/modals';
import { useStore } from 'models/Provider';
import { UserDepartment } from 'types/users';

type Props = ModalProps<{
  userId: number;
}>;

type DepartmentOptions = {
  value: string;
  label: string;
  canEditFullAccess: boolean;
};

export const AddUserToDepartmentModal = (props: Props) => {
  const { current, userStore, t } = useStore();
  const { closeModal, userId } = props;
  const { showError, showSuccess } = useToaster();

  const userDepartmentsQuery = useQuery({
    queryKey: ['departments', userId],
    queryFn: () => api.users.loadDepartments(userId),
    enabled: current.info.isEnterprise,
    select: useCallback((data) => {
      return (data ?? []).map((department: UserDepartment) => department.departmentId);
    }, []),
  });

  const departmentsQuery = useQuery({
    queryKey: ['departments'],
    queryFn: () => api.departments.list(),
    enabled: userDepartmentsQuery.isSuccess,
    select: useCallback(
      (data) => {
        return (data ?? [])
          .filter((department: Department) => !userDepartmentsQuery.data.includes(department.id))
          .map((department: Department) => ({
            value: department.id.toString(),
            label: department.translations.find(
              (translation) => translation.language === current.info.defaultLanguage
            )?.title,
            canEditFullAccess: department.canEditFullAccess,
          }));
      },
      [current.info.defaultLanguage, userDepartmentsQuery.data]
    ),
  });
  const [departmentId, setDepartmentId] = useState('');
  const [fullAccess, setFullAccess] = useState(false);

  const queryClient = useQueryClient();
  const submit = useMutation<{}, SubmitError>(
    () => api.departments.addUser(Number(departmentId), userId.toString(), fullAccess),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['departments']);
        userStore.load(true); // To load the user's updated departments
        showSuccess(t('modals.adddepartmentuser.success'));
        closeModal();
      },
      onError: (error) => {
        showError(error.response ? error.response.data.error : error.message);
      },
    }
  );

  if (!departmentsQuery.isSuccess) {
    return <Loading />;
  }

  return (
    <Stack space={6}>
      <div>
        <Text font="heading1" text={t('modals.adddepartmentuser.header')} />
        <Text font="regular16" text={t('modals.adddepartmentuser.subheader')} />
      </div>
      <Stack space={4}>
        <Stack space={0}>
          <Select
            label={t('modals.adddepartmentuser.department.label')}
            options={departmentsQuery.data}
            onChange={setDepartmentId}
            noOptions={() => t('modals.adddepartmentuser.department.nooptions')}
            placeholder={t('common.select')}
          />
        </Stack>
        <Stack space={0}>
          <Stack space={1} orientation="horizontal" align="center">
            <Text font="semibold14" text={t('modals.adddepartmentuser.fullaccess.label')} />
            <IconInfoSmall
              color="purple50"
              tooltip={t('modals.adddepartmentuser.fullaccess.tooltip')}
            />
          </Stack>
          <Stack space={0} orientation="horizontal" justify={'space-between'}>
            <Text text={fullAccess ? t('common.yes') : t('common.no')} />
            <Switch
              onChange={setFullAccess}
              label={t('modals.adddepartmentuser.fullaccess.label')}
              labelVisible={false}
              defaultValue={fullAccess}
              isDisabled={
                !(
                  departmentId &&
                  departmentsQuery.data.some(
                    (department: DepartmentOptions) =>
                      Number(department.value) === Number(departmentId) &&
                      department.canEditFullAccess
                  )
                )
              }
            />
          </Stack>
        </Stack>
        <Text
          color="purple80"
          font="regular14"
          text={t('modals.adddepartmentuser.fullaccess.description')}
        />
        {fullAccess && (
          <Text
            color="red100"
            font="regular14"
            text={t('modals.adddepartmentuser.fullaccess.yousure')}
          />
        )}
      </Stack>
      <Stack orientation="horizontal" space={4} justify="flex-end">
        <Button
          variant="secondary"
          onClick={closeModal}
          text={t('modals.adddepartmentuser.cancel')}
        />
        <Button
          isDisabled={!departmentId || submit.isLoading}
          onClick={() => submit.mutate()}
          text={t('modals.adddepartmentuser.save')}
          variant="primary-blue"
        />
      </Stack>
    </Stack>
  );
};
